// src/pages/RemixPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import './RemixPage.css';
import AdvancedRemixPage from './AdvancedRemixPage';

function RemixPage({ onBack }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedMeasure, setSelectedMeasure] = useState(null);
  const [range, setRange] = useState({ start: 0, end: 31 });
  const [playheadX, setPlayheadX] = useState(20); 
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  
  const [showExitModal, setShowExitModal] = useState(false);

  const [magicStates, setMagicStates] = useState({
    reverb: false,
    lowPass: true,
    highPass: false
  });

  const waveformRef = useRef(null);
  const measuresCount = 32;
  const measureWidth = 60; 
  const paddingLeft = 20;

  const stopSwipe = (e) => {
    e.stopPropagation();
  };

  const generateWavePath = () => {
    let d = `M 0 60`;
    for (let i = 0; i < measuresCount * measureWidth; i += 10) {
      const height = Math.random() * 50 + 10;
      d += ` L ${i} ${60 - height} L ${i + 5} ${60 + height}`;
    }
    return d;
  };

  const wavePath = useRef(generateWavePath()).current;

  useEffect(() => {
    let anim;
    if (isPlaying && !isDraggingPlayhead) {
      const startTime = Date.now();
      const startX = playheadX;
      const speed = 0.06; // px per ms
      
      const step = () => {
        const elapsed = Date.now() - startTime;
        let newX = startX + (elapsed * speed);
        
        const maxLimit = (range.end + 1) * measureWidth + paddingLeft;
        const startLimit = (range.start * measureWidth) + paddingLeft;
        
        if (newX > maxLimit) {
            const loopDuration = (maxLimit - startLimit) / speed;
            const currentLoopTime = elapsed % loopDuration;
            newX = startLimit + (currentLoopTime * speed);
        }

        setPlayheadX(newX);
        anim = requestAnimationFrame(step);
      };
      anim = requestAnimationFrame(step);
    }
    return () => cancelAnimationFrame(anim);
  }, [isPlaying, range, isDraggingPlayhead]); 

  const toggleMagic = (key) => {
    setMagicStates(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleMeasureClick = (index) => {
    if (selectedMeasure === index) {
      setSelectedMeasure(null);
    } else {
      setSelectedMeasure(index);
    }
  };

  // 重置出入点逻辑
  const resetRange = () => {
    setRange({ start: 0, end: measuresCount - 1 });
    // 可选：将播放头也归位到开头
    setPlayheadX(paddingLeft);
  };

  const setStartPoint = () => {
    if (selectedMeasure > range.end) {
        setRange({ start: selectedMeasure, end: selectedMeasure });
    } else {
        setRange(prev => ({ ...prev, start: selectedMeasure }));
    }
    setSelectedMeasure(null);
    setPlayheadX(selectedMeasure * measureWidth + paddingLeft);
  };

  const setEndPoint = () => {
     if (selectedMeasure < range.start) {
        setRange({ start: selectedMeasure, end: selectedMeasure });
    } else {
        setRange(prev => ({ ...prev, end: selectedMeasure }));
    }
    setSelectedMeasure(null);
  };

  const handleDragStart = (e) => {
    e.stopPropagation(); 
    setIsDraggingPlayhead(true);
  };

  const handleDragMove = (e) => {
    if (!isDraggingPlayhead || !waveformRef.current) return;
    if(e.cancelable) e.preventDefault();

    const rect = waveformRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const scrollLeft = waveformRef.current.scrollLeft;
    
    let relX = clientX - rect.left + scrollLeft;
    relX = Math.max(0, Math.min(relX, measuresCount * measureWidth + paddingLeft * 2));
    
    setPlayheadX(relX);
  };

  const handleDragEnd = () => {
    setIsDraggingPlayhead(false);
  };

  useEffect(() => {
    if (isDraggingPlayhead) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDraggingPlayhead]);

  const handleBackAttempt = () => {
    setShowExitModal(true);
  };

  const confirmExit = () => {
    setShowExitModal(false);
    if (onBack) onBack();
  };

  if (showAdvanced) {
    return <AdvancedRemixPage onBack={() => setShowAdvanced(false)} />;
  }

  return (
    <div 
        className="remix-page"
        onTouchStart={stopSwipe}
        onMouseDown={stopSwipe}
    >
      <div className="remix-header">
        <button className="icon-btn" onClick={handleBackAttempt}>
          <i className="fas fa-times"></i>
        </button>
        <h2>音乐魔法棒</h2>
        {/* 修改右上角按钮：重置出入点 */}
        <button className="icon-btn" onClick={resetRange}>
          <i className="fas fa-arrows-left-right"></i>
        </button>
      </div>

      <div className="waveform-editor">
        <div 
            className="waveform-scroll-container" 
            ref={waveformRef}
            onTouchStart={(e) => e.stopPropagation()} 
        >
          {/* 增加足够的宽度缓冲，确保容器不挤压子元素 */}
          <div className="waveform-content" style={{ width: measuresCount * measureWidth + paddingLeft + 100 }}>
            <div 
              className="waveform-visual-layer" 
              style={{ paddingLeft: paddingLeft }} // 这里的Padding与JS逻辑保持一致
            >
              <svg 
                className="waveform-svg" 
                width={measuresCount * measureWidth} 
                height="120" 
                viewBox={`0 0 ${measuresCount * measureWidth} 120`} 
                preserveAspectRatio="none"
                style={{ left: paddingLeft }} // 确保SVG起始位置与Padding一致
              >
                 <path d={wavePath} stroke="#764ba2" strokeWidth="2" fill="none" />
              </svg>

              {Array.from({ length: measuresCount }).map((_, idx) => {
                const isSelected = selectedMeasure === idx;
                const isOutOfRange = idx < range.start || idx > range.end;
                
                return (
                  <div 
                    key={idx}
                    className={`measure-rect ${isSelected ? 'selected' : ''} ${isOutOfRange ? 'out-of-range' : 'in-range'}`}
                    onClick={() => handleMeasureClick(idx)}
                  >
                  </div>
                );
              })}
            </div>

            {selectedMeasure !== null && (
               <div 
                 className="popover-positioner" 
                 // JS位置计算逻辑不变，现在CSS布局修复后，这个计算将与视觉对齐
                 style={{ left: selectedMeasure * measureWidth + paddingLeft + (measureWidth / 2) }}
                 onClick={(e) => e.stopPropagation()}
               >
                 <div className="comic-popover-content">
                    <button className="pop-btn" onClick={setStartPoint}>设为起点</button>
                    <button className="pop-btn" onClick={setEndPoint}>设为终点</button>
                 </div>
               </div>
            )}

            <div className="waveform-playhead-layer">
                <div 
                    className={`playhead ${isDraggingPlayhead ? 'dragging' : ''}`} 
                    style={{ transform: `translateX(${playheadX}px)` }}
                >
                    <div 
                        className="playhead-handle-top"
                        onMouseDown={handleDragStart}
                        onTouchStart={handleDragStart}
                    >
                        <i className="fas fa-grip-lines-vertical"></i>
                    </div>
                </div>
            </div>

          </div>
        </div>
      </div>

      <div className="transport-controls">
        <button className="transport-side-btn">
            <i className="fas fa-undo"></i>
            <span>撤销</span>
        </button>
        
        <button 
            className={`transport-main-btn ${isPlaying ? 'playing' : ''}`} 
            onClick={() => setIsPlaying(!isPlaying)}
        >
          <i className={`fas fa-${isPlaying ? 'pause' : 'play'}`}></i>
        </button>

        <button className="transport-side-btn" onClick={() => {
            alert('保存成功！');
            if (onBack) onBack();
        }}>
             <i className="fas fa-check"></i>
             <span>合并</span>
        </button>
      </div>

      <div className="magic-cards-container">
        
        <div className="magic-card decompose-card" onClick={() => setShowAdvanced(true)}>
            <div className="card-title">
                <span>
                    <i className="fas fa-layer-group" style={{marginRight:10}}></i>
                    分解魔法
                </span>
            </div>
            <i className="fas fa-chevron-right"></i>
        </div>

        <div className="magic-card">
            <div className="card-title">节奏魔法</div>
            <div className="magic-split-content">
                <button className="vertical-btn">随机节奏</button>
                <div className="magic-slider-container">
                    <span className="magic-slider-label">BPM</span>
                    <div className="visual-slider" style={{background: 'linear-gradient(to top, #667eea 70%, #f0f0f0 70%)'}}></div>
                </div>
            </div>
        </div>

        <div className="magic-card">
            <div className="card-title">和弦魔法</div>
            <div className="magic-split-content">
                <button className="vertical-btn">随机和弦</button>
                <div className="magic-slider-container">
                     <span className="magic-slider-label">Pitch</span>
                     <div className="visual-slider" style={{background: 'linear-gradient(to top, #764ba2 40%, #f0f0f0 40%)'}}></div>
                </div>
            </div>
        </div>

        <div className="magic-card" onClick={() => toggleMagic('reverb')}>
            <div className="card-title">
                混响术
                <div className={`status-light ${magicStates.reverb ? 'on' : 'off'}`}></div>
            </div>
            <div style={{display:'flex', alignItems:'center', justifyContent:'center', flex:1}}>
                <i className="fas fa-broadcast-tower" style={{fontSize:'32px', opacity: magicStates.reverb ? 1 : 0.2, color: '#667eea', transition:'opacity 0.2s'}}></i>
            </div>
        </div>

        <div className="magic-card" onClick={() => toggleMagic('lowPass')}>
            <div className="card-title">
                低通术
                <div className={`status-light ${magicStates.lowPass ? 'on' : 'off'}`}></div>
            </div>
             <div style={{display:'flex', alignItems:'center', justifyContent:'center', flex:1}}>
                <i className="fas fa-align-left" style={{fontSize:'32px', opacity: magicStates.lowPass ? 1 : 0.2, color: '#667eea', transition:'opacity 0.2s'}}></i>
            </div>
        </div>

        <div className="magic-card" onClick={() => toggleMagic('highPass')}>
            <div className="card-title">
                高通术
                <div className={`status-light ${magicStates.highPass ? 'on' : 'off'}`}></div>
            </div>
             <div style={{display:'flex', alignItems:'center', justifyContent:'center', flex:1}}>
                <i className="fas fa-align-right" style={{fontSize:'32px', opacity: magicStates.highPass ? 1 : 0.2, color: '#667eea', transition:'opacity 0.2s'}}></i>
            </div>
        </div>

      </div>

      {showExitModal && (
        <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
            <div className="modal-box">
                <h3 className="modal-title">确定要退出吗？</h3>
                <p className="modal-desc">当前的修改尚未保存，退出后将丢失进度。</p>
                <div className="modal-buttons">
                    <button className="modal-btn btn-cancel" onClick={() => setShowExitModal(false)}>
                        取消
                    </button>
                    <button className="modal-btn btn-confirm" onClick={confirmExit}>
                        不保存退出
                    </button>
                </div>
            </div>
        </div>
      )}

    </div>
  );
}

export default RemixPage;